import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Download, Link2, Check } from "lucide-react";
import { toast } from "sonner";

const PDF_URL = `${process.env.REACT_APP_BACKEND_URL}/api/story.pdf`;

export default function StoryPage() {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${PDF_URL}`);
      setCopied(true);
      toast.success("Share link copied — send it to anyone");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Copy failed — the link is in your address bar");
    }
  };

  return (
    <div data-testid="story-page" className="min-h-screen bg-forest flex flex-col">
      <header className="border-b border-cream/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between gap-4">
          <Link to="/" data-testid="story-back-home" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-cream/60 hover:text-amber transition-colors duration-300">
            <ArrowLeft size={15} /> Back to Site
          </Link>
          <span className="inline-block bg-cream px-3 py-1.5 rounded-sm">
            <img src="/logo.png" alt="LUSH Adventure @ Barn" className="h-8 w-auto" />
          </span>
        </div>
      </header>

      <main className="flex-1 max-w-[1600px] w-full mx-auto px-6 md:px-12 py-10 md:py-14 flex flex-col">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8"
        >
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-amber mb-4" data-testid="story-label">Our Story</p>
            <h1 className="font-display text-4xl md:text-5xl tracking-tight text-cream leading-[1.05]">
              The LUSH Adventure <span className="italic text-amber">@ Barn</span> Story
            </h1>
            <p className="mt-4 text-base text-cream/70 max-w-xl leading-relaxed">
              The full story of the estate — our philosophy, zones, programmes and the sustainable
              journey of the Barn House. Read it here or share it with a link.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              data-testid="story-copy-link"
              onClick={copyLink}
              className="flex items-center gap-2 border border-amber/60 text-amber text-xs tracking-[0.2em] uppercase px-6 py-3.5 hover:bg-amber hover:text-night transition-colors duration-300"
            >
              {copied ? <Check size={15} /> : <Link2 size={15} />}
              {copied ? "Copied" : "Copy Share Link"}
            </button>
            <a
              data-testid="story-download"
              href={PDF_URL}
              download="LUSH-Adventure-Barn-Story.pdf"
              className="flex items-center gap-2 bg-amber text-night text-xs tracking-[0.2em] uppercase font-semibold px-6 py-3.5 hover:bg-cream transition-colors duration-300"
            >
              <Download size={15} /> Download PDF
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="flex-1 border border-cream/15 bg-night overflow-hidden min-h-[70vh]"
          data-testid="story-viewer"
        >
          <iframe
            src={`${PDF_URL}#toolbar=1&view=FitH`}
            title="LUSH Adventure @ Barn Story"
            className="w-full h-full min-h-[70vh]"
            data-testid="story-pdf-frame"
          />
        </motion.div>

        <p className="mt-4 text-xs text-cream/40" data-testid="story-direct-link">
          Direct link to share: {`${typeof window !== "undefined" ? window.location.origin : ""}${PDF_URL}`}
        </p>
      </main>
    </div>
  );
}
